import React, { useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { KeyOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import moment from "moment";
import _ from "lodash";

// Firebase
import firebase from "firebase/compat/app";

import Certificates from "./Certificates";

const { Paragraph, Text } = Typography;

// Accès API (décisions 01-02/10/2026) : les administrateurs de la société gèrent ici leurs
// comptes de service (expéditeurs techniques des envois déposés par l'API), leurs clés et leurs
// certificats clients (exigés pour la LRE). Option activée par Solidoc. En connexion déléguée
// (support Solidoc), l'écran est en lecture seule et les clés ne sont pas visibles : les règles et
// les Cloud Functions l'imposent aussi. Les certificats, publics, restent consultables.

function formatDate(timestamp) {
  return timestamp ? moment(timestamp.toDate()).format("DD/MM/YYYY") : "—";
}

function formatDateTime(timestamp) {
  return timestamp ? moment(timestamp.toDate()).format("DD/MM/YYYY HH:mm") : "Jamais";
}

function keyStatus(key) {
  if (key.revoked_at) return { label: "Révoquée", color: "default", active: false };
  if (key.expires_at && key.expires_at.toDate() < new Date()) {
    return { label: "Expirée", color: "orange", active: false };
  }
  return { label: "Active", color: "green", active: true };
}

function ServiceAccountModal({ visible, account, onSubmit, onCancel, loading }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) form.resetFields();
  }, [visible, account, form]);

  return (
    <Modal
      visible={visible}
      title={account ? "Modifier le compte de service" : "Nouveau compte de service"}
      okText={account ? "Enregistrer" : "Créer"}
      cancelText="Annuler"
      maskClosable={false}
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() =>
        form
          .validateFields()
          .then((values) => onSubmit(values))
          .catch(() => {})
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          name: account?.name || "",
          notification_email: account?.notification_email || "",
        }}
      >
        <Form.Item
          name="name"
          label="Nom"
          extra="Il apparaît comme « Déclenché par » dans l'historique des envois (ex. : Service Paie)."
          rules={[
            { required: true, whitespace: true, message: "Veuillez entrer un nom" },
            { max: 80, message: "80 caractères au plus" },
          ]}
        >
          <Input placeholder="Ex : Service Paie" />
        </Form.Item>
        <Form.Item
          name="notification_email"
          label="E-mail de notification (facultatif)"
          extra="Reçoit les e-mails Solidoc liés aux envois de ce compte (preuves, anomalies), parfois un par destinataire : prévoyez une boîte partagée. Sans adresse, aucun e-mail n'est envoyé."
          rules={[{ type: "email", message: "Veuillez entrer un e-mail valide" }]}
        >
          <Input placeholder="Ex : paie@votre-societe.fr" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function ApiKeyModal({ visible, account, onSubmit, onCancel, loading }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) form.resetFields();
  }, [visible, account, form]);

  return (
    <Modal
      visible={visible}
      title={`Nouvelle clé — ${account?.name || ""}`}
      okText="Générer la clé"
      cancelText="Annuler"
      maskClosable={false}
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() =>
        form
          .validateFields()
          .then((values) => onSubmit(values))
          .catch(() => {})
      }
    >
      <Form layout="vertical" form={form} initialValues={{ label: "", expires_at: null }}>
        <Form.Item
          name="label"
          label="Libellé"
          extra="Pour reconnaître la clé (ex. : Production, Recette, Logiciel de paie)."
          rules={[
            { required: true, whitespace: true, message: "Veuillez entrer un libellé" },
            { max: 80, message: "80 caractères au plus" },
          ]}
        >
          <Input placeholder="Ex : Production" />
        </Form.Item>
        <Form.Item
          name="expires_at"
          label="Date d'expiration (facultative)"
          extra="Sans date, la clé reste valable jusqu'à sa révocation."
        >
          <DatePicker
            format="DD/MM/YYYY"
            style={{ width: "100%" }}
            disabledDate={(current) => current && current < moment().endOf("day")}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function RevealKeyModal({ revealed, onClose }) {
  return (
    <Modal
      visible={Boolean(revealed)}
      title="Votre nouvelle clé d'API"
      closable={false}
      maskClosable={false}
      footer={
        <Button type="primary" onClick={onClose}>
          J'ai copié la clé
        </Button>
      }
    >
      <Alert
        type="warning"
        showIcon
        message="Copiez cette clé maintenant : elle ne sera plus jamais affichée."
        description="Transmettez-la par un canal sûr à l'équipe qui intègre l'API. En cas de perte ou de fuite, révoquez-la et générez-en une nouvelle."
        style={{ marginBottom: 16 }}
      />
      {revealed && (
        <>
          <Paragraph copyable={{ text: revealed.key, tooltips: ["Copier", "Copié"] }} code>
            {revealed.key}
          </Paragraph>
          <Text type="secondary">
            {revealed.expires_at
              ? `Expire le ${moment(revealed.expires_at).format("DD/MM/YYYY")}.`
              : "Sans date d'expiration."}
          </Text>
        </>
      )}
    </Modal>
  );
}

export default function ApiAccess() {
  const user = useSelector((state) => state.user, shallowEqual);
  const functions = firebase.app().functions("europe-west1");
  const companyId = user?.company?.id;

  const [ready, setReady] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  const [accountModal, setAccountModal] = useState({ visible: false, account: null });
  const [keyModal, setKeyModal] = useState({ visible: false, account: null });
  const [revealed, setRevealed] = useState(null);
  const [saving, setSaving] = useState(false);

  const allowed = user && user.is_company_admin && user.companyData?.api_access;

  // Usurpation = connexion par jeton personnalisé : même signal que les règles et les fonctions
  useEffect(() => {
    const current = firebase.auth().currentUser;
    if (!current) return;
    current
      .getIdTokenResult()
      .then((result) => setImpersonating(result.signInProvider === "custom"))
      .catch(() => setImpersonating(false))
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!allowed || !ready || !companyId) return undefined;
    let cancelled = false;
    setLoading(true);

    const db = firebase.firestore();
    const companyRef = db.collection("companies").doc(companyId);
    // Le filtre sur la société est exigé par les règles pour une requête de liste
    const accountsQuery = db
      .collection("service_accounts")
      .where("company", "==", companyRef)
      .get();
    // Clés : jamais lues en connexion déléguée (refusé par les règles de toute façon)
    const keysQuery = impersonating
      ? Promise.resolve(null)
      : db.collection("api_keys").where("company", "==", companyRef).get();

    Promise.all([accountsQuery, keysQuery])
      .then(([accountsSnap, keysSnap]) => {
        if (cancelled) return;
        setAccounts(
          _.sortBy(
            accountsSnap.docs.map((doc) => ({ id: doc.id, key: doc.id, ...doc.data() })),
            (account) => (account.name || "").toLowerCase()
          )
        );
        setKeys(
          keysSnap ? keysSnap.docs.map((doc) => ({ id: doc.id, key: doc.id, ...doc.data() })) : []
        );
      })
      .catch((e) => {
        console.error("Error loading API access:", e);
        message.error("Erreur lors du chargement de l'accès API");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allowed, ready, impersonating, lastUpdate, companyId]);

  const keysByAccount = useMemo(
    () => _.groupBy(keys, (key) => key.service_account && key.service_account.id),
    [keys]
  );

  if (!allowed) {
    return <Redirect to="/parametres/compte" />;
  }

  async function runCallable(name, payload, successMessage) {
    setSaving(true);
    try {
      const { data } = await functions.httpsCallable(name)(payload);
      if (successMessage) message.success(successMessage);
      setLastUpdate(Date.now());
      return data;
    } catch (error) {
      console.error(`Error calling ${name}:`, error);
      message.error(error?.message || "Une erreur est survenue. Veuillez réessayer.");
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAccount(values) {
    const account = accountModal.account;
    const payload = {
      name: values.name.trim(),
      notification_email: (values.notification_email || "").trim(),
    };
    const result = account
      ? await runCallable(
          "updateServiceAccount",
          { serviceAccountId: account.id, ...payload },
          "Compte de service enregistré"
        )
      : await runCallable("createServiceAccount", payload, "Compte de service créé");
    if (result) setAccountModal({ visible: false, account: null });
  }

  async function handleCreateKey(values) {
    const result = await runCallable("createApiKey", {
      serviceAccountId: keyModal.account.id,
      label: values.label.trim(),
      expires_at: values.expires_at ? values.expires_at.format("YYYY-MM-DD") : null,
    });
    if (result) {
      setKeyModal({ visible: false, account: null });
      setRevealed(result);
    }
  }

  const keyColumns = [
    { title: "Libellé", dataIndex: "label", key: "label" },
    {
      title: "Clé",
      key: "prefix",
      render: (_value, record) => <Text code>{`${record.key_prefix}…`}</Text>,
    },
    {
      title: "Créée le",
      key: "created_at",
      render: (_value, record) => formatDate(record.created_at),
    },
    {
      title: "Expire le",
      key: "expires_at",
      render: (_value, record) => (record.expires_at ? formatDate(record.expires_at) : "Jamais"),
    },
    {
      title: "Dernière utilisation",
      key: "last_used_at",
      render: (_value, record) => formatDateTime(record.last_used_at),
    },
    {
      title: "Statut",
      key: "status",
      render: (_value, record) => {
        const status = keyStatus(record);
        return <Tag color={status.color}>{status.label}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_value, record) =>
        record.revoked_at ? null : (
          <Popconfirm
            title="Révoquer cette clé ? Les appels qui l'utilisent seront refusés immédiatement."
            okText="Révoquer"
            okType="danger"
            cancelText="Annuler"
            onConfirm={() => runCallable("revokeApiKey", { keyId: record.id }, "Clé révoquée")}
          >
            <Button size="small" danger>
              Révoquer
            </Button>
          </Popconfirm>
        ),
    },
  ];

  const accountColumns = [
    { title: "Nom", dataIndex: "name", key: "name" },
    {
      title: "E-mail de notification",
      dataIndex: "notification_email",
      key: "notification_email",
      render: (value) => value || <Text type="secondary">Aucun</Text>,
    },
    {
      title: "Statut",
      key: "status",
      render: (_value, record) =>
        record.is_active ? <Tag color="green">Actif</Tag> : <Tag>Désactivé</Tag>,
    },
  ];

  if (!impersonating) {
    accountColumns.push(
      {
        title: "Clés actives",
        key: "keys",
        render: (_value, record) =>
          (keysByAccount[record.id] || []).filter((key) => keyStatus(key).active).length,
      },
      {
        title: "Dernière utilisation",
        key: "last_used_at",
        render: (_value, record) => {
          const lastUsed = _.maxBy(
            (keysByAccount[record.id] || []).filter((key) => key.last_used_at),
            (key) => key.last_used_at.toMillis()
          );
          return formatDateTime(lastUsed && lastUsed.last_used_at);
        },
      },
      {
        title: "Actions",
        key: "actions",
        render: (_value, record) => (
          <Space size="small" wrap>
            <Button
              size="small"
              icon={<KeyOutlined />}
              disabled={!record.is_active}
              onClick={() => setKeyModal({ visible: true, account: record })}
            >
              Générer une clé
            </Button>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => setAccountModal({ visible: true, account: record })}
            />
            {record.is_active ? (
              <Popconfirm
                title="Désactiver ce compte ? Toutes ses clés seront refusées par l'API."
                okText="Désactiver"
                okType="danger"
                cancelText="Annuler"
                onConfirm={() =>
                  runCallable(
                    "updateServiceAccount",
                    { serviceAccountId: record.id, is_active: false },
                    "Compte de service désactivé"
                  )
                }
              >
                <Button size="small" danger>
                  Désactiver
                </Button>
              </Popconfirm>
            ) : (
              <Button
                size="small"
                onClick={() =>
                  runCallable(
                    "updateServiceAccount",
                    { serviceAccountId: record.id, is_active: true },
                    "Compte de service réactivé"
                  )
                }
              >
                Réactiver
              </Button>
            )}
          </Space>
        ),
      }
    );
  }

  return (
    <>
      <h1>Accès API</h1>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Déposez vos envois depuis vos logiciels"
        description={
          <>
            Un compte de service représente un service ou un flux de votre société (ex. : Service
            Paie). Les envois déposés avec ses clés apparaissent dans l'historique avec « Compte de
            service » comme déclencheur, visibles des administrateurs. Une clé n'est affichée qu'une
            seule fois, à sa création : conservez-la dans un coffre à secrets. La lettre recommandée
            électronique n'est pas encore ouverte via l'API : elle exigera en plus un certificat
            client enregistré sur le compte (dépliez la ligne du compte).
          </>
        }
      />

      {impersonating && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Mode support : lecture seule"
          description="Les comptes de service sont gérés par les administrateurs du client. Les clés ne sont pas visibles en connexion déléguée ; les certificats (publics) sont consultables."
        />
      )}

      {!impersonating && (
        <div style={{ textAlign: "right", marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAccountModal({ visible: true, account: null })}
          >
            Nouveau compte de service
          </Button>
        </div>
      )}

      <Table
        bordered
        size="small"
        scroll={{ x: true }}
        pagination={false}
        loading={loading}
        dataSource={accounts}
        columns={accountColumns}
        locale={{ emptyText: "Aucun compte de service" }}
        expandable={{
          // Usurpation : certificats seuls, en lecture ; les clés ne sont jamais lues
          expandedRowRender: (record) => (
            <>
              {!impersonating && (
                <>
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    Clés d'API
                  </Text>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={_.orderBy(
                      keysByAccount[record.id] || [],
                      (key) => (key.created_at ? key.created_at.toMillis() : 0),
                      "desc"
                    )}
                    columns={keyColumns}
                    locale={{ emptyText: "Aucune clé" }}
                  />
                </>
              )}
              <Certificates
                account={record}
                companyId={companyId}
                readOnly={impersonating}
                runCallable={runCallable}
                saving={saving}
                lastUpdate={lastUpdate}
              />
            </>
          ),
        }}
      />

      <ServiceAccountModal
        visible={accountModal.visible}
        account={accountModal.account}
        loading={saving}
        onSubmit={handleSaveAccount}
        onCancel={() => setAccountModal({ visible: false, account: null })}
      />
      <ApiKeyModal
        visible={keyModal.visible}
        account={keyModal.account}
        loading={saving}
        onSubmit={handleCreateKey}
        onCancel={() => setKeyModal({ visible: false, account: null })}
      />
      <RevealKeyModal revealed={revealed} onClose={() => setRevealed(null)} />
    </>
  );
}
